import { CUSTOM_SCHEDULE_MIN_PLAN, planAtLeast, WIDGET_CHANNEL, WIDGET_ONLY_TRIGGERS, WORKFLOW_CHANNELS, WORKFLOW_INTENT_ACTIONS, WORKFLOW_SCHEDULES, type WorkflowSchedule, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { ChoiceChips, Field, Icon, Select } from "@sagot/ui";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import type { WorkflowSettings } from "../types";
import { ChannelNotes } from "./channel-notes";
import { ConditionEditor } from "./condition-editor";
import { CustomScheduleEditor } from "./custom-schedule";
import { GoalField } from "./goal-field";
import { HomeOptionNote } from "./home-option-note";
import { useRefs } from "./refs";
import { defaultCustomSchedule } from "./schedule-model";
import { useDefaultWorkflow } from "./system/default-context";
import { hasTriggerOptions, TriggerOptionsEditor } from "./trigger-options";

const B = "engage.workflows.builder";
const D = "engage.workflows.defaults.builder";

interface SettingsCardProps {
  trigger: WorkflowTrigger;
  settings: WorkflowSettings;
  /** The workflow has steps the visitor sees: say how channels render them. */
  hasFacing: boolean;
  disabled: boolean;
  onChange: (settings: WorkflowSettings) => void;
}

/** The "When" field: office hours choices, plus a custom weekly schedule from the Pro plan on. */
function ScheduleField({ settings, disabled, onChange }: Pick<SettingsCardProps, "settings" | "disabled" | "onChange">) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const inDefault = useDefaultWorkflow() !== null;
  const customLocked = (inDefault || (plan !== undefined && !planAtLeast(plan, CUSTOM_SCHEDULE_MIN_PLAN))) && settings.schedule !== "custom";
  const pick = (schedule: WorkflowSchedule) => {
    if (schedule === "custom") onChange({ ...settings, schedule, custom_schedule: settings.custom_schedule ?? defaultCustomSchedule() });
    else onChange({ ...settings, schedule, custom_schedule: undefined });
  };
  return (
    <div className="space-y-3">
      <Field label={t(`${B}.schedule`)}>
        <Select value={settings.schedule} disabled={disabled} onChange={(e) => { pick(e.target.value as WorkflowSchedule); }}>
          {WORKFLOW_SCHEDULES.map((schedule) => (
            <option key={schedule} value={schedule} disabled={schedule === "custom" && customLocked}>
              {t(`${B}.schedules.${schedule}`)}
              {schedule === "custom" && customLocked ? ` (${inDefault ? t(`${D}.notInDefaults`) : t(`console.plans.${CUSTOM_SCHEDULE_MIN_PLAN}`)})` : ""}
            </option>
          ))}
        </Select>
      </Field>
      {settings.schedule === "custom" ? (
        <CustomScheduleEditor schedule={settings.custom_schedule ?? defaultCustomSchedule()} disabled={disabled} onChange={(custom_schedule) => { onChange({ ...settings, custom_schedule }); }} />
      ) : null}
    </div>
  );
}

/** The trigger (fixed) and who / when it runs for: agents, channels, schedule, conditions. */
export function SettingsCard({ trigger, settings, hasFacing, disabled, onChange }: SettingsCardProps) {
  const { t } = useTranslation();
  const { agents } = useRefs();
  const defaults = useDefaultWorkflow();
  return (
    <section className="space-y-5 rounded-lg border-2 border-primary bg-surface p-4" aria-labelledby="workflow-trigger" data-testid="workflow-settings">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-on-primary">
          <Icon name="zap" className="size-5" />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-primary-ink">{t(`${B}.trigger`)}</p>
          <h2 id="workflow-trigger" className="font-bold">{t(`engage.workflows.triggers.${trigger}`)}</h2>
          <p className="text-xs text-fg-subtle">{t(`engage.workflows.triggerHints.${trigger}`)}</p>
          {trigger === "home_option" ? <HomeOptionNote /> : null}
          {defaults ? (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-fg-muted" data-testid="trigger-locked">
              <Icon name="lock" className="mt-0.5 size-3.5 shrink-0" />
              {t("engage.workflows.defaults.builder.triggerLocked")}
            </p>
          ) : null}
        </div>
      </div>
      {hasTriggerOptions(trigger) ? (
        <div className="space-y-4 border-t border-line pt-4" data-testid="trigger-options">
          <p className="text-sm font-bold">{t(`${B}.options.title`)}</p>
          <TriggerOptionsEditor trigger={trigger} options={settings.trigger_options ?? {}} disabled={disabled} onChange={(trigger_options) => { onChange({ ...settings, trigger_options }); }} />
        </div>
      ) : null}
      <div className="space-y-4 border-t border-line pt-4">
        <p className="text-sm font-bold">{t(`${B}.settings`)}</p>
        <div>
          <span className="label">{t(`${B}.agents`)}</span>
          <ChoiceChips
            multiple
            aria-label={t(`${B}.agents`)}
            value={settings.agent_ids}
            onValueChange={(agent_ids) => { if (!disabled) onChange({ ...settings, agent_ids }); }}
            options={agents.map((agent) => ({ value: agent.id, label: agent.name, disabled }))}
          />
          {settings.agent_ids.length === 0 ? <p className="mt-1 text-xs text-fg-subtle">{t(`${B}.allAgents`)}</p> : null}
        </div>
        <div>
          <span className="label">{t(`${B}.channels`)}</span>
          <ChoiceChips
            multiple
            aria-label={t(`${B}.channels`)}
            value={settings.channels}
            onValueChange={(channels) => { if (!disabled) onChange({ ...settings, channels }); }}
            options={WORKFLOW_CHANNELS.map((channel) => ({ value: channel, label: t(`engage.channels.${channel}`), disabled: disabled || (WIDGET_ONLY_TRIGGERS.has(trigger) && channel !== WIDGET_CHANNEL) }))}
          />
          {settings.channels.length === 0 ? <p className="mt-1 text-xs text-fg-subtle">{t("engage.workflows.allChannels")}</p> : null}
          {hasFacing ? <div className="mt-2"><ChannelNotes channels={settings.channels} /></div> : null}
        </div>
        <ScheduleField settings={settings} disabled={disabled} onChange={onChange} />
        <div>
          <span className="label">{t(`${B}.conditions`)}</span>
          <p className="mb-2 text-xs text-fg-subtle">{t(`${B}.conditionsHint`)}</p>
          {trigger === "intent_detected" ? <p className="mb-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted">{t(`${B}.intentHint`, { actions: WORKFLOW_INTENT_ACTIONS.join(", ") })}</p> : null}
          <ConditionEditor value={settings.conditions} disabled={disabled} onChange={(conditions) => { onChange({ ...settings, conditions }); }} />
        </div>
        {defaults ? null : <GoalField goal={settings.goal} disabled={disabled} onChange={(goal) => { onChange({ ...settings, goal }); }} />}
      </div>
    </section>
  );
}
